import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { ExternalLink, AlertTriangle } from 'lucide-react';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { logger } from '@/lib/logger';

interface TeamSubscription {
  plan_tier: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  trial_ends_at: string | null;
  pending_downgrade_tier: string | null;
  pending_downgrade_date: string | null;
}

interface BillingManagementProps {
  subscription: TeamSubscription;
  onSubscriptionChange?: () => void | Promise<void>;
}

export function BillingManagement({ subscription, onSubscriptionChange }: BillingManagementProps) {
  const { toast } = useToast();
  const { getEffectiveUserId } = useImpersonation();
  const [loading, setLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [canceling, setCanceling] = useState(false);
  const [isResuming, setIsResuming] = useState(false);

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      // CRITICAL: Use getEffectiveUserId() which returns:
      // - The impersonated user's ID if impersonating
      // - The actual logged-in user's ID if not impersonating
      const effectiveUserId = await getEffectiveUserId();
      
      if (!effectiveUserId) {
        throw new Error('Not authenticated');
      }
      
      // Get auth user for logging
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      
      const { data, error } = await supabase.functions.invoke('create-polar-portal', {
        body: {
          returnUrl: `${window.location.origin}/settings?tab=billing`,
          // CRITICAL: Pass the effective user ID (handles impersonation correctly)
          effectiveUserId: effectiveUserId,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      logger.error('Portal error:', error);
      toast({
        title: 'Portal Access Failed',
        description: error instanceof Error ? error.message : 'Failed to open billing portal',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (confirmText.toLowerCase() !== 'unsubscribe') {
      toast({
        title: 'Confirmation Required',
        description: 'Please type "unsubscribe" to confirm',
        variant: 'destructive',
      });
      return;
    }

    setCanceling(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription');

      if (error) throw error;

      toast({
        title: 'Subscription Canceled',
        description: data?.message || 'Your subscription has been scheduled for cancellation.',
      });

      setCancelDialogOpen(false);
      setConfirmText('');

      // Refresh subscription data immediately
      if (onSubscriptionChange) {
        await onSubscriptionChange();
      } else {
        // Fallback to page reload if no refresh function provided
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (error) {
      logger.error('Cancel error:', error);
      toast({
        title: 'Cancellation Failed',
        description: error instanceof Error ? error.message : 'Failed to cancel subscription',
        variant: 'destructive',
      });
    } finally {
      setCanceling(false);
    }
  };

  const handleResumeSubscription = async () => {
    setIsResuming(true);
    try {
      const { data, error } = await supabase.functions.invoke('resume-subscription');

      if (error) throw error;

      toast({
        title: 'Subscription Resumed',
        description: data.message || 'Your subscription has been successfully resumed.',
      });

      // Refresh subscription data immediately
      if (onSubscriptionChange) {
        await onSubscriptionChange();
      } else {
        // Fallback to page reload if no refresh function provided
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (error) {
      logger.error('Error resuming subscription:', error);
      toast({
        title: 'Failed to Resume Subscription',
        description: error instanceof Error ? error.message : 'Could not resume your subscription',
        variant: 'destructive',
      });
    } finally {
      setIsResuming(false);
    }
  };

  // Check if subscription is canceling
  const isCanceling = subscription.status === 'canceling' || subscription.pending_downgrade_tier !== null;
  const cancelDate = subscription.pending_downgrade_date 
    ? new Date(subscription.pending_downgrade_date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Manage Subscription</CardTitle>
          <CardDescription>
            Update your payment method, view invoices, or cancel your subscription
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-3 flex-wrap">
          <Button
            onClick={handleManageSubscription}
            disabled={loading}
            className="gap-2 shadow-lg hover:shadow-xl transition-all"
          >
            {loading ? 'Loading...' : (
              <>
                Open Billing Portal
                <ExternalLink className="h-4 w-4" />
              </>
            )}
          </Button>
          {isCanceling ? (
            <Button
              onClick={handleResumeSubscription}
              disabled={isResuming}
              variant="outline"
              className="gap-2 border-green-600 text-green-700 hover:bg-green-100 hover:text-green-800 hover:border-green-700 dark:border-green-500 dark:text-green-400 dark:hover:bg-green-950/60 dark:hover:text-green-300 dark:hover:border-green-400 transition-colors font-medium"
            >
              {isResuming ? 'Resuming...' : 'Resume Subscription'}
            </Button>
          ) : (
            <Button
              onClick={() => setCancelDialogOpen(true)}
              variant="destructive"
              className="gap-2"
            >
              <AlertTriangle className="h-4 w-4" />
              Cancel Subscription
            </Button>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Cancel Subscription
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-4 text-sm text-muted-foreground">
            <p>
              Are you sure you want to cancel your subscription? This will:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Schedule cancellation at the end of your current billing period</li>
              <li>You'll keep access to your current plan until then</li>
              <li>After cancellation, your subscription will end and you'll need to resubscribe to continue using North Reports</li>
            </ul>
            <div className="space-y-2 pt-4">
              <Label htmlFor="confirm-text">
                Type <span className="font-bold">unsubscribe</span> to confirm
              </Label>
              <Input
                id="confirm-text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="unsubscribe"
                className="font-mono"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmText('')}>
              Keep Subscription
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              disabled={confirmText.toLowerCase() !== 'unsubscribe' || canceling}
              className="bg-destructive hover:bg-destructive/90"
            >
              {canceling ? 'Canceling...' : 'Cancel Subscription'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
